/**
 * EpicOverviewForm - Reusable form for Epic overview fields
 * 
 * Used in:
 * - EpicDetailModal (edit mode)
 * - EpicMergeModal (source view + merged form)
 * 
 * Props:
 * - formData: { title, description, status, target_date, persona_ids, problem_statement, initiative_id }
 * - onChange: (field, value) => void
 * - isEditing: boolean (controls whether fields are editable)
 * - isDisabled: boolean (overrides isEditing for locked states)
 * - colors: theme colors from useSafeMode
 * - initiatives: array of available initiatives for dropdown
 * - personas: array of available personas for selection
 * - parentDeadlines: array of {date, label} for calendar markers
 * - initiativeDetails: object with initiative data (for target date warning)
 * - errors: { [field]: string } - validation errors to display
 * - mode: 'edit' | 'create' | 'view'
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { MarkdownEditor } from '../ui/markdown-editor';
import { cn } from '../../lib/utils';
import useFeatureFlags, { FEATURE_IDS } from '../../hooks/useFeatureFlags';
import { Users, AlertTriangle, AlertCircle, X } from 'lucide-react';

// Status options for epics (matches Initiative - gold standard)
export const EPIC_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'planning', label: 'Planning', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-500' },
  { value: 'shipped', label: 'Shipped', color: 'bg-green-500' },
  { value: 'paused', label: 'Paused', color: 'bg-purple-500' },
];

// Required fields for epic
export const EPIC_REQUIRED_FIELDS = ['title'];

const EpicOverviewForm = ({
  formData,
  onChange,
  isEditing = true,
  isDisabled = false,
  colors,
  initiatives = [],
  personas = [],
  parentDeadlines = [],
  initiativeDetails = null,
  errors = {},
  mode = 'edit',
}) => {
  const { isEnabled } = useFeatureFlags();
  const showInitiatives = isEnabled(FEATURE_IDS.TAB_INITIATIVES);
  const showPersonas = isEnabled(FEATURE_IDS.TAB_PERSONAS);
  
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  // Helper to get error styling for a field
  const getFieldErrorClass = (field) => {
    return errors[field] ? 'border-red-500 ring-1 ring-red-500' : '';
  };

  const effectiveDisabled = isDisabled || !isEditing;
  const statusConfig = EPIC_STATUS_OPTIONS.find(s => s.value === formData.status) || EPIC_STATUS_OPTIONS[0];

  // Check if target date exceeds parent initiative's date
  const hasDateWarning = formData.target_date && 
    initiativeDetails?.target_date && 
    new Date(formData.target_date) > new Date(initiativeDetails.target_date);

  return (
    <div className="space-y-4">
      {/* Alert: Epic target date exceeds Initiative target date */}
      {hasDateWarning && (
        <div className={cn(
          "flex items-center gap-3 p-3 rounded-lg border",
          "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700"
        )}>
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Target date exceeds Initiative deadline
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              This Epic's target date ({new Date(formData.target_date).toLocaleDateString()}) is after the parent Initiative's target date ({new Date(initiativeDetails.target_date).toLocaleDateString()}).
            </p>
          </div>
        </div>
      )}

      {/* Key Attributes Section */}
      <div className={cn("space-y-4 pb-5 border-b", colors.borderPrimary)}>
        {/* Row 1: Epic Name & Status */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className={cn(colors.textSecondary, errors.title && "text-red-500")}>
              Epic Name <span className="text-red-500">*</span>
            </Label>
            {isEditing && !isDisabled ? (
              <>
                <Input
                  value={formData.title || ''}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., User Authentication"
                  disabled={effectiveDisabled}
                  className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, getFieldErrorClass('title'))}
                  data-testid="epic-title-input"
                />
                {errors.title && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.title}
                  </p>
                )}
              </>
            ) : (
              <p className={cn("mt-1 text-lg font-medium", colors.textPrimary)}>
                {formData.title || 'Untitled Epic'}
              </p>
            )}
          </div>
          <div>
            <Label className={colors.textSecondary}>Status</Label>
            {isEditing ? (
              <Select 
                value={formData.status || 'draft'} 
                onValueChange={(v) => handleChange('status', v)}
                disabled={effectiveDisabled}
              >
                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  {EPIC_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <div className={cn("w-2 h-2 rounded-full", opt.color)} />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1">
                <Badge variant="outline" className={cn(
                  formData.status === 'shipped' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                  formData.status === 'in_progress' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' :
                  formData.status === 'planning' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                  formData.status === 'paused' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                )}>
                  {statusConfig.label}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Initiative & Target Date */}
        <div className={`grid ${showInitiatives ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
          {showInitiatives && (
          <div>
            <Label className={colors.textSecondary}>Initiative</Label>
            {isEditing ? (
              <Select 
                value={formData.initiative_id || 'none'} 
                onValueChange={(v) => handleChange('initiative_id', v === 'none' ? '' : v)}
                disabled={effectiveDisabled}
              >
                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="Select initiative" />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  <SelectItem value="none">Not linked</SelectItem>
                  {initiatives.map(init => (
                    <SelectItem key={init.initiative_id} value={init.initiative_id}>
                      {init.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className={cn("mt-1", colors.textPrimary)}>
                {initiatives.find(i => i.initiative_id === formData.initiative_id)?.title || 'Not linked'}
              </p>
            )}
          </div>
          )}
          <div>
            <Label className={colors.textSecondary}>Target Date</Label>
            {isEditing && !isDisabled ? (
              <div className="mt-1">
                <DatePickerWithMarkers
                  value={formData.target_date || ''}
                  onChange={(date) => handleChange('target_date', date)}
                  parentDeadlines={parentDeadlines}
                  placeholder="Select target date"
                  disabled={effectiveDisabled}
                />
              </div>
            ) : (
              <p className={cn("mt-1", colors.textPrimary)}>
                {formData.target_date 
                  ? new Date(formData.target_date).toLocaleDateString() 
                  : 'Not specified'}
              </p>
            )}
          </div>
        </div>

        {/* Row 3: Personas */}
        <div>
          <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
            <Users className="w-3 h-3" /> Personas
          </Label>
          <div className={cn(
            "mt-1 flex flex-wrap items-center gap-2 min-h-[38px] p-2 rounded-lg border",
            colors.bgInput, colors.borderPrimary,
            !isEditing && "opacity-70"
          )}>
            {(formData.persona_ids || []).map(personaId => {
              const persona = personas.find(p => p.persona_id === personaId);
              return persona ? (
                <span
                  key={personaId}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                >
                  {persona.name}
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => handleChange('persona_ids', formData.persona_ids.filter(id => id !== personaId))}
                      className="ml-0.5 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ) : null;
            })}
            {isEditing && (
              <Select 
                value=""
                onValueChange={(v) => {
                  if (v && !formData.persona_ids?.includes(v)) {
                    handleChange('persona_ids', [...(formData.persona_ids || []), v]);
                  }
                }}
              >
                <SelectTrigger className="w-auto min-w-[100px] h-7 text-xs border-0 bg-transparent focus:ring-0 p-0">
                  <SelectValue placeholder={formData.persona_ids?.length ? "Add more..." : "Add personas..."} />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  {personas
                    .filter(p => !formData.persona_ids?.includes(p.persona_id))
                    .map(p => (
                      <SelectItem key={p.persona_id} value={p.persona_id}>{p.name}</SelectItem>
                    ))
                  }
                  {personas.filter(p => !formData.persona_ids?.includes(p.persona_id)).length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-gray-500">All personas selected</div>
                  )}
                </SelectContent>
              </Select>
            )}
            {(!formData.persona_ids || formData.persona_ids.length === 0) && !isEditing && (
              <span className={cn("text-sm", colors.textMuted)}>No personas linked</span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <Label className={colors.textSecondary}>Description</Label>
        {isEditing && !isDisabled ? (
          <div className="mt-1">
            <MarkdownEditor
              content={formData.description || ''}
              onChange={(value) => handleChange('description', value)}
              placeholder="Additional details about this epic..."
              minHeight={80}
              disabled={effectiveDisabled}
            />
          </div>
        ) : (
          <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
            {formData.description ? (
              <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                <ReactMarkdown>{formData.description}</ReactMarkdown>
              </div>
            ) : (
              <p className={cn("text-sm", colors.textMuted)}>No description</p>
            )}
          </div>
        )}
      </div>

      {/* Problem Statement */}
      <div>
        <Label className={colors.textSecondary}>Problem Statement</Label>
        {isEditing && !isDisabled ? (
          <div className="mt-1">
            <MarkdownEditor
              content={formData.problem_statement || ''}
              onChange={(value) => handleChange('problem_statement', value)}
              placeholder="What problem does this epic solve? Why does it matter?"
              minHeight={80}
              disabled={effectiveDisabled}
            />
          </div>
        ) : (
          <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
            {formData.problem_statement ? (
              <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                <ReactMarkdown>{formData.problem_statement}</ReactMarkdown>
              </div>
            ) : (
              <p className={cn("text-sm", colors.textMuted)}>No problem statement defined yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EpicOverviewForm;
